import { Box, Divider, Typography } from "@mui/material";
import type { FC, ReactElement } from "react";
import { useTranslation } from "react-i18next";
import { SystemRoundedCheckbox } from "../../../../components/UIToolkit/Clickable/SystemRoundedCheckbox";
import { SystemDropdown } from "../../../../components/UIToolkit/Inputs/SystemDropDown";
import { regularFieldSx, smallLabelTypographySx, V1_TEXT_MAX_WIDTH } from "./consts";
import { LocationEmrMappingSectionV2 } from "./LocationEmrMappingSectionV2";
import type { EmrMapping } from "./locationEmrMapping";
import type { RoomsNotice } from "./useCensusAreaOptions";

/** How the v2 census area arranges its controls (see `layout`). */
export type LocationEmrMappingLayout = "stacked" | "row";

type LocationEmrMappingSectionCommonProps = {
	availableUnits: string[];
	availableFloors: string[];
	availableRooms: string[];
	mapping: EmrMapping;
	departmentWideCensus: boolean;
	onDepartmentWideCensusChange: (checked: boolean) => void;
	/** Ratio only: the ladder needs an explicit choice, so the area fields lock while Department wide is ticked. */
	lockAreasWhenDepartmentWide: boolean;
	onMappingChange: (changes: Partial<EmrMapping>) => void;
	onDropdownOpen: () => void;
	onDropdownClose: () => void;
};

/**
 * v1's host shows the rooms loading / failed notices itself (and hides the section when there's nothing to map
 * to). v2 shows them inside, so it takes the notice; its host renders it while `emrOptionsAvailable` or a notice
 * is up.
 */
export type LocationEmrMappingSectionProps = LocationEmrMappingSectionCommonProps &
	(
		| { design: "v1" }
		| {
				design: "v2";
				/** Why the rooms can't be listed yet (`roomsNoticeFor` of the census check), or null. */
				roomsNotice: RoomsNotice | null;
				/**
				 * `stacked` (the default): Department wide over the unit and floor, over the rooms. `row`: all of them on one
				 * line that wraps as the space narrows, for a host that keeps the census area to a line or two.
				 */
				layout?: LocationEmrMappingLayout;
				/** Read-only: every control shows its value but takes no input. */
				disabled: boolean;
		  }
	);

/**
 * Where a location's census comes from: an EMR area (unit, floor, rooms) or the whole department. The
 * two are exclusive. In a ratio department the area dropdowns are disabled while "Department wide census"
 * is checked; elsewhere they stay open and choosing an area unchecks it (see `censusChoiceAfterAreaChange`).
 */
export const LocationEmrMappingSection: FC<LocationEmrMappingSectionProps> = (props): ReactElement => {
	const {
		availableUnits,
		availableFloors,
		availableRooms,
		mapping,
		departmentWideCensus,
		onDepartmentWideCensusChange,
		lockAreasWhenDepartmentWide,
		onMappingChange,
		onDropdownOpen,
		onDropdownClose,
	} = props;
	const { t } = useTranslation();
	const noneOption = { value: "", label: t("schedule.template.v3.editor.none") };
	const areasDisabled = lockAreasWhenDepartmentWide && departmentWideCensus;
	switch (props.design) {
		case "v1":
			return (
				<>
					<Box sx={{ display: "flex", flexDirection: "column", gap: 1, width: "100%", alignItems: "start" }}>
						<Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
							<SystemRoundedCheckbox
								checked={departmentWideCensus}
								onChange={onDepartmentWideCensusChange}
								label={t("schedule.template.census.department.wide")}
								data-testid="location-department-wide-census-checkbox"
							/>
							<Typography sx={{ ...smallLabelTypographySx, maxWidth: V1_TEXT_MAX_WIDTH }}>{t("schedule.template.census.department.wide.helper")}</Typography>
						</Box>
						{(availableUnits.length > 0 || availableFloors.length > 0) && (
							<Box sx={{ display: "flex", gap: 1, width: "100%" }}>
								{availableUnits.length > 0 && (
									<SystemDropdown
										onOpen={onDropdownOpen}
										onClose={onDropdownClose}
										label={t("schedule.template.v3.editor.unit")}
										selectSx={regularFieldSx}
										disabled={areasDisabled}
										items={[noneOption, ...availableUnits.map((unit) => ({ value: unit, label: unit }))]}
										// DEFAULT: no unit selects the "None" option, whose value is "".
										selectedItem={mapping.unit ?? ""}
										onChange={(item) => onMappingChange({ unit: item === "" ? undefined : item })}
										data-testid="location-emr-unit-dropdown"
									/>
								)}
								{availableFloors.length > 0 && (
									<SystemDropdown
										onOpen={onDropdownOpen}
										onClose={onDropdownClose}
										label={t("schedule.template.v3.editor.floor")}
										selectSx={regularFieldSx}
										disabled={areasDisabled}
										items={[noneOption, ...availableFloors.map((floor) => ({ value: floor, label: floor }))]}
										// DEFAULT: no floor selects the "None" option, whose value is "".
										selectedItem={mapping.floor ?? ""}
										onChange={(item) => onMappingChange({ floor: item === "" ? undefined : item })}
										data-testid="location-emr-floor-dropdown"
									/>
								)}
							</Box>
						)}
						{availableRooms.length > 0 && (
							<SystemDropdown
								multiple
								label={t("schedule.template.v3.editor.rooms")}
								formControlSx={{ width: "100%" }}
								disabled={areasDisabled}
								items={availableRooms.map((room) => ({ value: room, label: room }))}
								selectedItems={mapping.rooms}
								onChangeMultiple={(rooms) => onMappingChange({ rooms })}
								onOpen={onDropdownOpen}
								onClose={onDropdownClose}
								data-testid="location-emr-rooms-dropdown"
							/>
						)}
					</Box>
					<Divider flexItem />
				</>
			);
		case "v2":
			return (
				<LocationEmrMappingSectionV2
					availableUnits={availableUnits}
					availableFloors={availableFloors}
					availableRooms={availableRooms}
					mapping={mapping}
					departmentWideCensus={departmentWideCensus}
					onDepartmentWideCensusChange={onDepartmentWideCensusChange}
					lockAreasWhenDepartmentWide={lockAreasWhenDepartmentWide}
					onMappingChange={onMappingChange}
					onDropdownOpen={onDropdownOpen}
					onDropdownClose={onDropdownClose}
					roomsNotice={props.roomsNotice}
					// DEFAULT: hosts that don't choose get the stacked layout the section shipped with.
					layout={props.layout ?? "stacked"}
					disabled={props.disabled}
				/>
			);
	}
};
